import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { AssignmentDto } from '@betreuung/shared';
import { overlapConflict, staleConflict } from '@/api/http';
import { useAppData } from '@/app/appData';

/**
 * The single entry point for creating and editing assignments.
 *
 * Every view that can start the flow (month range selection, day timeline, the
 * conflict dialog's "open the existing one") goes through here, which is what
 * keeps two rules in exactly one place:
 *
 *  - Editing anything that reaches into the past asks for confirmation first.
 *  - A refused save discards the draft. The specification is explicit that the
 *    user must then open the conflicting assignment intentionally; silently
 *    keeping the draft around would invite a blind retry.
 */

export interface AssignmentDraft {
  mode: 'create' | 'edit';
  assignmentId?: number;
  expectedUpdatedAt?: number;
  startSlot: number;
  endSlot: number;
  /** `null` means "Nicht zugewiesen", which clears the range on save. */
  personId: number | null;
}

/** Range whose confirmation is pending, plus what to do once confirmed. */
interface PastConfirmation {
  startSlot: number;
  endSlot: number;
  personId: number | null;
  proceed: () => void;
}

interface FlowsValue {
  draft: AssignmentDraft | null;
  pastConfirmation: PastConfirmation | null;
  conflict: AssignmentDto | null;
  stale: { current: AssignmentDto | null } | null;

  /** Opens the modal for a new assignment over the given range. */
  openCreate: (range: { startSlot: number; endSlot: number }, personId?: number | null) => void;
  /** Opens the modal for an existing assignment, asking about the past first. */
  openEdit: (assignment: AssignmentDto) => void;
  updateDraft: (patch: Partial<AssignmentDraft>) => void;
  closeDraft: () => void;

  confirmPast: () => void;
  cancelPast: () => void;

  /** Called by the modal when a save was refused. */
  reportSaveError: (error: unknown) => boolean;
  dismissConflict: () => void;
  /** Opens the assignment the conflict referred to. */
  openConflicting: () => void;
  dismissStale: () => void;
  openStale: () => void;
}

const FlowsContext = createContext<FlowsValue | null>(null);

export function AssignmentFlowsProvider({ children }: { children: ReactNode }) {
  const { nowSlot, user, canWrite } = useAppData();
  const [draft, setDraft] = useState<AssignmentDraft | null>(null);
  const [pastConfirmation, setPastConfirmation] = useState<PastConfirmation | null>(null);
  const [conflict, setConflict] = useState<AssignmentDto | null>(null);
  const [stale, setStale] = useState<{ current: AssignmentDto | null } | null>(null);

  // Every path into the assignment modal goes through openCreate or openEdit,
  // and every mutation lives inside that modal. Refusing here is therefore what
  // actually makes a view-only session read-only, rather than each affordance
  // remembering to check for itself. Those affordances are hidden as well, so
  // this is the backstop, not the user-visible behaviour.
  const openCreate = useCallback(
    (range: { startSlot: number; endSlot: number }, personId?: number | null) => {
      if (!canWrite) return;
      setDraft({
        mode: 'create',
        startSlot: range.startSlot,
        endSlot: range.endSlot,
        // Defaults to whoever is signed in: assigning yourself is the common case
        // and, more importantly, it keeps "Speichern" from clearing the range by
        // accident, which pre-selecting "Nicht zugewiesen" would do. Safe as a
        // default only because a view-only account never gets this far — they are
        // the one signed-in person who cannot receive care time.
        personId: personId === undefined ? user.personId : personId,
      });
    },
    [canWrite, user.personId],
  );

  const openEdit = useCallback(
    (assignment: AssignmentDto) => {
      if (!canWrite) return;
      const open = () =>
        setDraft({
          mode: 'edit',
          assignmentId: assignment.id,
          expectedUpdatedAt: assignment.updatedAt,
          startSlot: assignment.startSlot,
          endSlot: assignment.endSlot,
          personId: assignment.personId,
        });

      if (assignment.startSlot < nowSlot) {
        setPastConfirmation({
          startSlot: assignment.startSlot,
          endSlot: assignment.endSlot,
          personId: assignment.personId,
          proceed: open,
        });
        return;
      }
      open();
    },
    [canWrite, nowSlot],
  );

  const updateDraft = useCallback((patch: Partial<AssignmentDraft>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const closeDraft = useCallback(() => setDraft(null), []);

  const confirmPast = useCallback(() => {
    setPastConfirmation((current) => {
      current?.proceed();
      return null;
    });
  }, []);

  const cancelPast = useCallback(() => setPastConfirmation(null), []);

  /**
   * Maps a failed save onto the right dialog. Returns whether the error was a
   * recognised conflict, so the modal knows whether to show a generic message.
   */
  const reportSaveError = useCallback((error: unknown): boolean => {
    const existing = overlapConflict(error);
    if (existing) {
      setDraft(null);
      setConflict(existing);
      return true;
    }
    const outdated = staleConflict(error);
    if (outdated) {
      setDraft(null);
      setStale(outdated);
      return true;
    }
    return false;
  }, []);

  const dismissConflict = useCallback(() => setConflict(null), []);
  const dismissStale = useCallback(() => setStale(null), []);

  const openConflicting = useCallback(() => {
    setConflict((current) => {
      if (current) openEdit(current);
      return null;
    });
  }, [openEdit]);

  const openStale = useCallback(() => {
    setStale((current) => {
      if (current?.current) openEdit(current.current);
      return null;
    });
  }, [openEdit]);

  const value = useMemo<FlowsValue>(
    () => ({
      draft,
      pastConfirmation,
      conflict,
      stale,
      openCreate,
      openEdit,
      updateDraft,
      closeDraft,
      confirmPast,
      cancelPast,
      reportSaveError,
      dismissConflict,
      openConflicting,
      dismissStale,
      openStale,
    }),
    [
      draft,
      pastConfirmation,
      conflict,
      stale,
      openCreate,
      openEdit,
      updateDraft,
      closeDraft,
      confirmPast,
      cancelPast,
      reportSaveError,
      dismissConflict,
      openConflicting,
      dismissStale,
      openStale,
    ],
  );

  return <FlowsContext.Provider value={value}>{children}</FlowsContext.Provider>;
}

export function useAssignmentFlows(): FlowsValue {
  const context = useContext(FlowsContext);
  if (!context) throw new Error('useAssignmentFlows must be used inside AssignmentFlowsProvider');
  return context;
}
